import { AdmProject } from "@custom-types/base-types";
import { EntityCellParams } from "@components/common/data-grid/adm-data-grid-types";
import { FaroCoreApiTypes, FaroCoreApiUtils } from "@stellar/core-api-client";
import {
  DataGridCell,
  DataGridDateCell,
  getEntity,
} from "@components/common/data-grid/adm-data-grid-utils";
import { isAdmProject, isCompanyContext } from "@utils/type-guards";
import { formatProjectArea, getMemberDisplayName } from "@utils/data-display";
import { ReactNode, useMemo } from "react";
import { useAppNavigation } from "@router/use-app-navigation";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import {
  GeneralTableEvents,
  MainPageEvents,
} from "@utils/track-event/track-event-list";
import { Chip, ChipProps, useToast } from "@flat-ui";
import { TableType } from "@utils/track-event/event-types";
import { DataGridOptionCell } from "@components/common/data-grid/data-grid-option-cell";
import { useGenerateRoutes } from "@router/router-helper";
import { formatProjectStorageSize } from "@utils/project-utils";
import { UiUtils } from "@stellar/utils";

type AllProjectTypes = AdmProject | FaroCoreApiTypes.AdmMembersProjects;

/** Return project Id cell in data-grid */
export function ProjectIdCell(
  params: EntityCellParams<AllProjectTypes>
): ReactNode {
  const entity = getEntity(params);
  const projectId = isAdmProject(entity) ? entity.id : entity.projectId;
  return <DataGridCell content={projectId} />;
}

/** Return project name cell in data-grid */
export function ProjectNameCell(
  params: EntityCellParams<AllProjectTypes>
): ReactNode {
  const entity = getEntity(params);
  const projectId = isAdmProject(entity) ? entity.id : entity.projectId;

  return (
    <DataGridCell
      content={entity.name}
      tooltip={`${entity.name} (${projectId})`}
    />
  );
}

/** Return project area cell in data-grid */
export function ProjectAreaCell(
  params: EntityCellParams<AllProjectTypes>
): ReactNode {
  const entity = getEntity(params);
  return (
    <DataGridCell
      content={
        isAdmProject(entity) ? formatProjectArea(entity.area) : UiUtils.DASH
      }
    />
  );
}

/** Return project createdAt cell in data-grid */
export function ProjectCreatedAtCell(
  params: EntityCellParams<AllProjectTypes>
): ReactNode {
  return <DataGridDateCell date={getEntity(params).createdAt} />;
}

/** Return project modifiedAt cell in data-grid */
export function ProjectModifiedAtCell(
  params: EntityCellParams<AllProjectTypes>
): ReactNode {
  return <DataGridDateCell date={getEntity(params).modifiedAt} />;
}

/** Return a cell to indicate if the project is standalone or not */
export function StandaloneCell(
  params: EntityCellParams<AllProjectTypes>
): ReactNode {
  const entity = getEntity(params);
  /**
   * Returns the properties for the chip to show if the project is standalone.
   * Returns null if the standalone property is missing in the entity.
   */
  const statusChipProps: ChipProps | null = useMemo(() => {
    if (!("standalone" in entity)) {
      return null;
    }
    return entity.standalone
      ? {
          label: "Yes",
          color: "info" as const,
          tooltip: "The project is standalone",
        }
      : {
          label: "No",
          color: "default" as const,
          tooltip: "The project is not standalone",
        };
  }, [entity]);

  if (!statusChipProps) {
    return null;
  }

  return <Chip {...statusChipProps} />;
}

/** Return project member role cell in data-grid */
export function ProjectMemberRoleCell(
  params: EntityCellParams<AllProjectTypes>
): ReactNode {
  const entity = getEntity(params);
  return (
    <DataGridCell
      content={isAdmProject(entity) ? UiUtils.DASH : entity.projectRole}
    />
  );
}

/** Return project groups cell in data-grid */
export function ProjectGroupsCell(
  params: EntityCellParams<AllProjectTypes>
): ReactNode {
  const entity = getEntity(params);
  return (
    <DataGridCell
      content={
        isAdmProject(entity) && isCompanyContext(entity.context)
          ? entity.context.group.name
          : UiUtils.DASH
      }
    />
  );
}

/** Return project archived state cell in data-grid */
export function ProjectArchivedStateCell(
  params: EntityCellParams<AllProjectTypes>
): ReactNode {
  const entity = getEntity(params);
  return isAdmProject(entity) ? (
    <DataGridCell
      content={FaroCoreApiUtils.Project.StateDisplayNames[entity.state]}
    />
  ) : (
    <DataGridCell content={entity.archived ? "Archived" : "Unarchived"} />
  );
}

/** Return project storage cell in data-grid */
export function ProjectStorageCell(
  params: EntityCellParams<AllProjectTypes>
): ReactNode {
  const entity = getEntity(params);

  return isAdmProject(entity) ? (
    <DataGridCell content={formatProjectStorageSize(entity.storage?.size)} />
  ) : (
    <DataGridCell content={UiUtils.DASH} />
  );
}

/** Return project workspace cell in data-grid */
export function ProjectWorkspaceCell(
  params: EntityCellParams<AllProjectTypes>
): ReactNode {
  const { trackEvent } = useTrackEvent();
  const { navigateToProfilePage } = useAppNavigation();
  const project = getEntity(params);

  if (isAdmProject(project) && isCompanyContext(project.context)) {
    return (
      <Chip
        textTransform="none"
        label={project.context.company.name}
        onClick={(event) => {
          event.stopPropagation();
          trackEvent({
            name: MainPageEvents.openItem,
            props: {
              target: "COMPANY",
              location: "search-projects-table",
            },
          });
          if (isCompanyContext(project.context)) {
            navigateToProfilePage({
              subjectType: "COMPANY",
              subjectId: project.context.company.id,
            });
          }
        }}
        color="primary"
        variant="outlined"
      />
    );
  }

  return <DataGridCell content={UiUtils.DASH} />;
}

/** Return project manager cell in data-grid */
export function ProjectManagerCell(
  params: EntityCellParams<AllProjectTypes>
): ReactNode {
  const { trackEvent } = useTrackEvent();
  const { navigateToProfilePage } = useAppNavigation();
  const entity = getEntity(params);
  if (!isAdmProject(entity)) {
    return <DataGridCell content={UiUtils.DASH} />;
  }

  const member = entity.projectManager;

  if (!FaroCoreApiUtils.Member.isMemberActive(member)) {
    return <DataGridCell content={getMemberDisplayName(member)} />;
  }

  return (
    <Chip
      textTransform="none"
      label={getMemberDisplayName(member)}
      onClick={(event) => {
        event.stopPropagation();
        trackEvent({
          name: MainPageEvents.openItem,
          props: {
            target: "USER",
            location: "search-projects-table",
          },
        });
        navigateToProfilePage({
          subjectType: "USER",
          subjectId: member.identity,
        });
      }}
      color="primary"
      variant="outlined"
    />
  );
}

/** Return project option cell in data-grid */
export function ProjectOptionsCell(
  params: EntityCellParams<AllProjectTypes>,
  tableType: TableType
): ReactNode {
  const { trackEvent } = useTrackEvent();
  const project = getEntity(params);
  const { generateProjectRoute } = useGenerateRoutes();
  const { openToast } = useToast();

  // This is needed to make sure ID exist as DataGridOptionCell needs an item with either id or identity to work
  const entity = isAdmProject(project)
    ? project
    : {
        ...project,
        id: project.projectId,
      };

  return (
    <DataGridOptionCell
      item={entity}
      menuItems={[
        {
          label: "Open project profile",
          onSelect: () => {
            trackEvent({
              name: MainPageEvents.openItem,
              props: {
                target: "PROJECT",
                location: tableType,
              },
            });
          },
          getHrefUrl: (entity) =>
            generateProjectRoute({
              projectId: entity.id,
            }).pathname,
        },
        {
          label: "Copy project ID",
          onSelect: async () => {
            trackEvent({
              name: GeneralTableEvents.copyRowIdToClipboard,
              props: {
                location: tableType,
              },
            });
            await navigator.clipboard.writeText(entity.id);
            openToast({
              title: "Project ID copied to clipboard!",
            });
          },
        },
      ]}
    />
  );
}
